import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import * as C from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CUSTOM_FIELD = "__custom__";

const emptyRule = () => ({
  name: "", description: "", title: "", display_text: "",
  roadmap_section: "recommended_first_steps", recommendation_type: "action",
  program_slug: "", priority: "medium", active: true, conditions: [],
});

function ConditionRow({ cond, programsLoading, onChange, onRemove }) {
  const known = C.RULE_FIELDS.find((f) => f.value === cond.field);
  const isCustom = cond._custom || (cond.field && !known);
  const op = C.RULE_OPERATORS.find((o) => o.value === cond.operator) || C.RULE_OPERATORS[0];
  const options = known?.options;

  const setField = (v) => {
    if (v === CUSTOM_FIELD) return onChange({ ...cond, field: "", _custom: true, value: "" });
    onChange({ ...cond, field: v, _custom: false, value: op.multi ? [] : "" });
  };
  const setOperator = (v) => {
    const nextOp = C.RULE_OPERATORS.find((o) => o.value === v);
    onChange({ ...cond, operator: v, value: nextOp?.multi ? (Array.isArray(cond.value) ? cond.value : []) : "" });
  };

  const toggleMulti = (val) => {
    const arr = Array.isArray(cond.value) ? cond.value : [];
    onChange({ ...cond, value: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  return (
    <div className="rounded-xl border border-kid-line p-3 bg-white space-y-2" data-testid="rule-condition-row">
      <div className="flex items-start gap-2">
        <div className="flex-1 grid sm:grid-cols-3 gap-2">
          {/* Field */}
          <Select value={isCustom ? CUSTOM_FIELD : (cond.field || undefined)} onValueChange={setField}>
            <SelectTrigger className="h-9 bg-white" data-testid="rule-cond-field"><SelectValue placeholder="Field" /></SelectTrigger>
            <SelectContent>
              {C.RULE_FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              <SelectItem value={CUSTOM_FIELD}>Custom field…</SelectItem>
            </SelectContent>
          </Select>
          {/* Operator */}
          <Select value={cond.operator} onValueChange={setOperator}>
            <SelectTrigger className="h-9 bg-white" data-testid="rule-cond-operator"><SelectValue placeholder="Operator" /></SelectTrigger>
            <SelectContent>
              {C.RULE_OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Value */}
          <div>
            {!op.needsValue ? (
              <div className="h-9 grid place-items-center text-xs text-kid-muted italic">no value needed</div>
            ) : options && !isCustom ? (
              op.multi ? (
                <div className="flex flex-wrap gap-1">
                  {options.map((o) => {
                    const on = Array.isArray(cond.value) && cond.value.includes(o.value);
                    return (
                      <button type="button" key={o.value} onClick={() => toggleMulti(o.value)}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-kid-teal text-white border-kid-teal" : "bg-white text-kid-muted border-kid-line"}`}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <Select value={cond.value || undefined} onValueChange={(v) => onChange({ ...cond, value: v })}>
                  <SelectTrigger className="h-9 bg-white" data-testid="rule-cond-value"><SelectValue placeholder="Value" /></SelectTrigger>
                  <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              )
            ) : (
              <Input className="h-9 bg-white" placeholder={op.multi ? "value1, value2" : "value"}
                value={op.multi ? (Array.isArray(cond.value) ? cond.value.join(", ") : "") : (cond.value || "")}
                onChange={(e) => onChange({ ...cond, value: op.multi ? e.target.value.split(",").map((s) => s.trim()).filter(Boolean) : e.target.value })}
                data-testid="rule-cond-value" />
            )}
          </div>
        </div>
        <button type="button" onClick={onRemove} className="text-kid-coral p-1.5 mt-1" data-testid="rule-cond-remove"><Trash2 size={16} /></button>
      </div>
      {isCustom && (
        <Input className="h-9 bg-white" placeholder="Custom field path, e.g. intake.answers.my_key"
          value={cond.field} onChange={(e) => onChange({ ...cond, field: e.target.value, _custom: true })} data-testid="rule-cond-custom-field" />
      )}
    </div>
  );
}

export default function RuleFormDialog({ open, onOpenChange, rule, programs, onSaved }) {
  const [form, setForm] = useState(emptyRule());
  const [saving, setSaving] = useState(false);
  const isEdit = !!rule?.id;

  useEffect(() => {
    if (open) setForm(rule?.id ? { ...emptyRule(), ...rule, program_slug: rule.program_slug || "" } : emptyRule());
  }, [open, rule]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addCondition = () => set("conditions", [...(form.conditions || []), { field: "", operator: "equals", value: "" }]);
  const updateCondition = (i, c) => set("conditions", form.conditions.map((x, idx) => (idx === i ? c : x)));
  const removeCondition = (i) => set("conditions", form.conditions.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name.trim() || !form.title.trim() || !form.display_text.trim()) {
      toast.error("Name, title and display text are required."); return;
    }
    // strip internal flags and empty conditions
    const conditions = (form.conditions || [])
      .filter((c) => c.field)
      .map(({ _custom, ...c }) => c);
    const payload = {
      name: form.name, description: form.description || null, title: form.title, display_text: form.display_text,
      roadmap_section: form.roadmap_section, recommendation_type: form.recommendation_type,
      program_slug: form.program_slug || null, priority: form.priority, active: form.active, conditions,
    };
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/admin/roadmap-rules/${rule.id}`, payload);
      else await api.post("/admin/roadmap-rules", payload);
      toast.success(isEdit ? "Rule updated." : "Rule created.");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rule-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit rule" : "New roadmap rule"}</DialogTitle>
          <DialogDescription>Define which families see this recommendation and where it appears.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Internal name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. OAP for diagnosed ON families" className="mt-1 bg-white" data-testid="rule-name" /></div>
            <div><Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1 bg-white" data-testid="rule-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{C.PRIORITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>

          <div><Label className="text-xs">Title (shown to family)</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Register for the Ontario Autism Program" className="mt-1 bg-white" data-testid="rule-title" /></div>

          <div><Label className="text-xs">Display text</Label>
            <Textarea value={form.display_text} onChange={(e) => set("display_text", e.target.value)} rows={2} placeholder="Plain-language explanation of this step." className="mt-1 bg-white" data-testid="rule-display-text" /></div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Section</Label>
              <Select value={form.roadmap_section} onValueChange={(v) => set("roadmap_section", v)}>
                <SelectTrigger className="mt-1 bg-white" data-testid="rule-section"><SelectValue /></SelectTrigger>
                <SelectContent>{C.ROADMAP_SECTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Type</Label>
              <Select value={form.recommendation_type} onValueChange={(v) => set("recommendation_type", v)}>
                <SelectTrigger className="mt-1 bg-white" data-testid="rule-type"><SelectValue /></SelectTrigger>
                <SelectContent>{C.RECOMMENDATION_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Linked program</Label>
              <Select value={form.program_slug || "none"} onValueChange={(v) => set("program_slug", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1 bg-white" data-testid="rule-program"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {programs.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Conditions (all must match — leave empty to show to everyone)</Label>
              <Button type="button" variant="outline" size="sm" onClick={addCondition} className="h-8" data-testid="rule-add-condition"><Plus size={14} className="mr-1" /> Add</Button>
            </div>
            <div className="space-y-2">
              {(form.conditions || []).length === 0 && <p className="text-xs text-kid-muted italic">No conditions — this rule applies to every family.</p>}
              {(form.conditions || []).map((c, i) => (
                <ConditionRow key={i} cond={c} onChange={(nc) => updateCondition(i, nc)} onRemove={() => removeCondition(i)} />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="rule-active" />
            <span className="text-sm text-kid-muted">{form.active ? "Active" : "Inactive"}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rule-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rule-save">{saving ? "Saving…" : isEdit ? "Save changes" : "Create rule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
